import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  buildCartMutationToken,
  cartLineIdentity,
  resolveCartLine,
  resolveEnhancedStepperErrorState,
} from '@theme/cart-items-logic';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { waitForGiftOperations } from '@theme/item-gifts';
import {
  beginCartPresentation, endCartPresentation, isCartPresentationPending,
  settleCartGifts, waitForCartPresentation,
} from '@theme/cart-update-coordinator';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {Map<string, { quantity: number, fallbackLine: number, lineItemKey?: string, previousQuantity?: number }>} */
  #pendingEnhancedStepperQuantities = new Map();
  /** @type {boolean} */
  #pendingPostMutationRefresh = false;
  #waitingForPresentation = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('click', this.#handleEnhancedQtyStepperClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('click', this.#handleEnhancedQtyStepperClick);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine } = event.detail;
    // A gift merge can move this selector while its change is debounced.
    // Resolve the emitting row's key, never the item now at its old index.
    const sourceRow = event.target instanceof Element ? event.target.closest('tr[data-key]') : null;
    const lineItemKey = sourceRow instanceof HTMLElement ? sourceRow.dataset.key : undefined;
    const line = this.#resolveCartLine(cartLine || 0, lineItemKey);
    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const requestToken = this.#buildCartMutationToken(line, lineItemKey);
    this.#pendingEnhancedStepperQuantities.delete(requestToken);

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      lineItemKey,
      requestToken,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles enhanced drawer quantity stepper clicks.
   * Keeps enhanced cart quantity updates on the same module path as other cart quantity changes.
   * @param {MouseEvent} event
   */
  #handleEnhancedQtyStepperClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-qty-stepper] [data-action]');
    if (!(button instanceof HTMLElement)) return;
    if (!this.contains(button)) return;

    event.preventDefault();

    const action = button.dataset.action;
    const line = Number(button.dataset.lineIndex || 0);
    if (!line || (action !== 'increase' && action !== 'decrease')) return;

    const stepper = button.closest('[data-qty-stepper]');
    const valueEl = stepper?.querySelector('[data-qty-value]');
    const row = button.closest('tr[data-quantity]');
    const lineItemKey = row?.dataset.key;
    const requestToken = this.#buildCartMutationToken(line, lineItemKey);

    const rowQuantity = Number(row?.dataset.quantity || 0);
    const currentQuantity = rowQuantity > 0 ? rowQuantity : Number(valueEl?.textContent || 1);
    if (!Number.isFinite(currentQuantity) || currentQuantity <= 0) return;

    const previousQuantity = currentQuantity;
    let nextQuantity = action === 'increase' ? currentQuantity + 1 : currentQuantity - 1;
    if (nextQuantity > 99) return;
    if (nextQuantity < 0) nextQuantity = 0;
    if (nextQuantity === currentQuantity) return;

    if (valueEl && nextQuantity > 0) {
      valueEl.textContent = String(nextQuantity);
    }
    if (row) {
      row.dataset.quantity = String(nextQuantity);
    }

    if (this.classList.contains('cart-items-disabled') || isCartPresentationPending()) {
      this.#pendingEnhancedStepperQuantities.set(requestToken, {
        quantity: nextQuantity,
        fallbackLine: line,
        lineItemKey: lineItemKey || undefined,
        previousQuantity,
        identity: this.#rowIdentity(row),
      });
      this.#resumeAfterPresentation();
      return;
    }

    this.#pendingEnhancedStepperQuantities.delete(requestToken);

    if (nextQuantity === 0) {
      this.onLineItemRemove(line);
      return;
    }

    this.updateQuantity({
      line,
      lineItemKey,
      requestToken,
      quantity: nextQuantity,
      previousQuantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  };

  /**
   * Applies the most recent queued enhanced stepper change after current cart mutation settles.
   */
  #flushPendingEnhancedStepper = () => {
    if (this.classList.contains('cart-items-disabled') || isCartPresentationPending()) return;
    if (!this.#pendingEnhancedStepperQuantities.size) return;

    while (this.#pendingEnhancedStepperQuantities.size && !this.classList.contains('cart-items-disabled') && !isCartPresentationPending()) {
      const [requestToken, pending] = this.#pendingEnhancedStepperQuantities.entries().next().value || [];
      if (!requestToken || !pending || typeof pending.quantity !== 'number') return;

      this.#pendingEnhancedStepperQuantities.delete(requestToken);

      let resolvedLine = this.#resolveCartLine(pending.fallbackLine, pending.lineItemKey);
      if (!resolvedLine && pending.identity) {
        const matches = Array.from(this.refs.cartItemRows).filter((row) => this.#rowIdentity(row) === pending.identity);
        if (matches.length === 1) {
          pending.lineItemKey = matches[0].dataset.key;
          resolvedLine = this.#resolveCartLine(0, pending.lineItemKey);
        }
      }
      if (!resolvedLine) continue;

      const lineItemRow = this.refs.cartItemRows[resolvedLine - 1];
      if (!lineItemRow) continue;

      lineItemRow.dataset.quantity = String(pending.quantity);
      const value = lineItemRow.querySelector('[data-qty-value]');
      const input = lineItemRow.querySelector('input[name="updates[]"], input[data-cart-quantity]');
      if (value) value.textContent = String(pending.quantity);
      if (input) input.value = String(pending.quantity);

      if (pending.quantity === 0) {
        this.onLineItemRemove(resolvedLine);
        return;
      }

      this.updateQuantity({
        line: resolvedLine,
        lineItemKey: pending.lineItemKey,
        requestToken,
        quantity: pending.quantity,
        previousQuantity: pending.previousQuantity,
        action: 'change',
      });

      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
      return;
    }
  };

  /**
   * Builds a stable mutation token for a cart line update.
   * @param {number} line
   * @param {string | undefined} lineItemKey
   * @returns {string}
   */
  #buildCartMutationToken = (line, lineItemKey) => {
    return buildCartMutationToken(line, lineItemKey);
  };

  /**
   * Resolves the current cart line index from a stable line-item key.
   * @param {number} fallbackLine
   * @param {string | undefined} lineItemKey
   * @returns {number}
   */
  #resolveCartLine = (fallbackLine, lineItemKey) => {
    return resolveCartLine(this.refs.cartItemRows, fallbackLine, lineItemKey);
  };

  /**
   * Schedules a post-mutation section refresh when external cart updates arrive
   * during an in-flight local cart mutation.
   */
  #queuePostMutationRefresh = () => {
    this.#pendingPostMutationRefresh = true;
  };

  /**
   * Runs a deferred refresh if one was queued while cart mutations were in flight.
   */
  #maybeRunPostMutationRefresh = () => {
    if (!this.#pendingPostMutationRefresh) return;
    if (this.classList.contains('cart-items-disabled') || isCartPresentationPending()) return;

    this.#pendingPostMutationRefresh = false;
    sectionRenderer.renderSection(this.sectionId, { cache: false });
  };

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const lineItemRowToRemove = this.refs.cartItemRows[line - 1];
    const lineItemKey = lineItemRowToRemove?.dataset.key;
    const requestToken = this.#buildCartMutationToken(line, lineItemKey);
    this.#pendingEnhancedStepperQuantities.delete(requestToken);

    this.updateQuantity({
      line,
      lineItemKey,
      requestToken,
      quantity: 0,
      action: 'clear',
    });

    if (!lineItemRowToRemove) return;

    const rowsToRemove = [
      lineItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === lineItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.lineItemKey] - Stable line-item key for race-safe updates.
   * @param {string} [config.requestToken] - Stable mutation token.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const { line, lineItemKey, quantity, previousQuantity } = config;
    if (!line || line < 1) return;
    const requestToken = config.requestToken || this.#buildCartMutationToken(line, lineItemKey);
    const row = this.refs.cartItemRows[line - 1];
    const identity = this.#rowIdentity(row);
    const transaction = beginCartPresentation();
    if (!transaction) {
      this.#pendingEnhancedStepperQuantities.set(requestToken, {
        quantity, fallbackLine: line, lineItemKey, previousQuantity, identity,
      });
      this.#resumeAfterPresentation();
      return;
    }
    const marker = cartPerformance.createStartingMarker(config.action + ":user-action");
    this.#disableCartItems();
    this.setAttribute('aria-busy', 'true');
    // Let an already-running gift operation finish before changing its source
    // product. Keep the UI locked separately from the native request lock.
    this.setAttribute('data-cart-gifts-settling', '');
    this.refs.cartTotal?.shimmer();
    const sections = [...new Set([this.sectionId, ...Array.from(
      document.querySelectorAll('cart-items-component[data-section-id]'), (item) => item.dataset.sectionId
    )])].filter(Boolean).slice(0, 5);
    let committed = false;
    let finalCart;
    let failure;
    try {
      await waitForGiftOperations();
      let currentKey = lineItemKey;
      if (identity) {
        const currentResponse = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (currentResponse.ok === false) throw Error('Could not verify the item being updated');
        const currentCart = await currentResponse.json();
        const matches = currentCart.items?.filter((item) => cartLineIdentity(item) === identity) || [];
        const exact = matches.find((item) => item.key === lineItemKey);
        if (!exact && matches.length !== 1) throw Error('The item changed while the cart was updating');
        currentKey = (exact || matches[0]).key;
      }
      this.removeAttribute('data-cart-gifts-settling');
      let response;
      try {
        response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
          body: JSON.stringify({ ...(currentKey ? { id: currentKey } : { line }), quantity }),
        }));
        const changed = JSON.parse(await response.text());
        if (changed.errors || response.ok === false) {
          this.#handleCartError(line, { errors: changed.errors || changed.description || 'Could not update this quantity.' },
            { lineItemKey, requestToken, previousQuantity });
          return;
        }
        committed = true;
      } finally {
        // Gift controllers can now work, while the old rows stay on screen.
        this.setAttribute('data-cart-gifts-settling', '');
      }
      try { await settleCartGifts(); }
      catch (error) { failure = error; }
      finalCart = await this.#readSettledCart(sections);
      await this.#renderSettledCart(finalCart);
      if (failure) this.#showUpdateError('Your quantity changed. We couldn’t finish checking the gift. Please try again.');
    } catch (error) {
      console.error(error);
      // A lost response may still have committed. Never restore a stale bundled
      // response or repeat the write when the outcome is uncertain.
      this.setAttribute('data-cart-gifts-settling', '');
      try {
        await settleCartGifts();
        finalCart = await this.#readSettledCart(sections);
        await this.#renderSettledCart(finalCart);
      } catch (_) { this.#pendingEnhancedStepperQuantities.clear(); }
      this.#showUpdateError(committed ? 'Your cart changed. Please check it and try again.' : 'We couldn’t confirm the update. Please check your cart and try again.');
    } finally {
      resetShimmer(this);
      this.removeAttribute('data-cart-gifts-settling');
      this.removeAttribute('aria-busy');
      this.#enableCartItems();
      endCartPresentation(transaction);
      this.#flushPendingEnhancedStepper();
      this.#maybeRunPostMutationRefresh();
      cartPerformance.measureFromMarker(marker);
    }
  }

  #rowIdentity(row) {
    if (!row?.dataset?.variantId) return null;
    try {
      return cartLineIdentity({ variant_id: row.dataset.variantId,
        selling_plan_id: row.dataset.sellingPlanId || null,
        properties: JSON.parse(row.dataset.lineProperties || '{}') });
    } catch (_) { return null; }
  }

  async #readSettledCart(sections) {
    const read = async (url, json = false) => {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (response.ok === false) throw Error('Could not refresh cart');
      return json ? response.json() : response.text();
    };
    const [cart, entries] = await Promise.all([
      read((window.Shopify?.routes?.root || "/") + "cart.js", true),
      Promise.all(sections.map(async (id) => {
        const url = new URL(window.location.href);
        url.searchParams.delete('sections'); url.searchParams.delete('view');
        url.searchParams.set('section_id', id);
        return [id, await read(url.toString())];
      })),
    ]);
    if (!Array.isArray(cart.items)) throw Error('Could not refresh cart');
    return { ...cart, sections: Object.fromEntries(entries) };
  }

  async #renderSettledCart(cart) {
    // Exact identities let queued clicks survive discount-driven key changes.
    // Never resolve an ambiguous line by variant or its old numeric position.
    for (const [token, pending] of [...this.#pendingEnhancedStepperQuantities]) {
      if (!pending.identity) continue;
      const matches = cart.items.filter((item) => cartLineIdentity(item) === pending.identity);
      const exact = matches.find((item) => item.key === pending.lineItemKey);
      if (!exact && matches.length !== 1) { this.#pendingEnhancedStepperQuantities.delete(token); continue; }
      const key = (exact || matches[0]).key;
      this.#pendingEnhancedStepperQuantities.delete(token);
      this.#pendingEnhancedStepperQuantities.set(this.#buildCartMutationToken(0, key), { ...pending, lineItemKey: key });
    }
    await morphSection(this.sectionId, cart.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
    this.#pendingPostMutationRefresh = false;
    this.#updateQuantitySelectors(cart);
    this.#updateCartQuantitySelectorButtonStates();
    this.dispatchEvent(new CartUpdateEvent(cart, this.sectionId, {
      itemCount: cart.item_count, source: 'cart-items-component', sections: cart.sections, giftsSettled: true,
    }));
  }

  #showUpdateError(message) {
    let error = this.querySelector('[data-cart-update-error]');
    if (!error) {
      error = document.createElement('p');
      error.setAttribute('data-cart-update-error', '');
      error.setAttribute('role', 'alert');
      this.append(error);
    }
    error.textContent = message;
  }

  #resumeAfterPresentation() {
    if (this.#waitingForPresentation) return;
    this.#waitingForPresentation = true;
    waitForCartPresentation().then(() => {
      this.#waitingForPresentation = false;
      if (!this.isConnected) return;
      this.#flushPendingEnhancedStepper();
      this.#maybeRunPostMutationRefresh();
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {{ lineItemKey?: string, requestToken?: string, previousQuantity?: number }} [options]
   */
  #handleCartError = (line, parsedResponseText, options = {}) => {
    if (options.requestToken) {
      this.#pendingEnhancedStepperQuantities.delete(options.requestToken);
    }

    const {
      resolvedLine,
      row,
      quantity: resetQuantity,
    } = resolveEnhancedStepperErrorState(this.refs.cartItemRows, line, options.lineItemKey, options.previousQuantity);

    if (!resolvedLine) {
      this.#queuePostMutationRefresh();
      return;
    }

    // Gift rows can omit selectors, so the selector list does not share the
    // cart-row indices. Find the control only inside the resolved item.
    const quantitySelector = row instanceof HTMLElement
      ? row.querySelector('cart-quantity-selector-component, quantity-selector-component') : undefined;
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const enhancedValue = row instanceof HTMLElement ? row.querySelector('[data-qty-value]') : null;
    if (enhancedValue) {
      enhancedValue.textContent = String(resetQuantity);
    }
    if (row instanceof HTMLElement) {
      row.dataset.quantity = String(resetQuantity);
    }

    const cartItemError = this.refs[`cartItemError-${resolvedLine}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${resolvedLine}`];

    if (!(cartItemError instanceof HTMLElement)) return;
    if (!(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (isCartPresentationPending() && !event.detail?.data?.giftsSettled) {
      this.#queuePostMutationRefresh();
      this.#resumeAfterPresentation();
      return;
    }
    if (event.detail?.data?.giftsSettled && event.target !== this) {
      const html = event.detail.data.sections?.[this.sectionId];
      this.#pendingPostMutationRefresh = false;
      if (html) morphSection(this.sectionId, html).then(() => this.#updateCartQuantitySelectorButtonStates());
      return;
    }
    if (event instanceof DiscountUpdateEvent) {
      if (this.classList.contains('cart-items-disabled')) {
        this.#queuePostMutationRefresh();
        return;
      }

      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    if (this.classList.contains('cart-items-disabled')) {
      this.#queuePostMutationRefresh();
      return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
